import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, Tag, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { formatPrice, formatDate, PROMO_TYPE_LABELS, PROMO_SCOPE_LABELS, PRICE_TYPE_LABELS } from '../../lib/utils';

const emptyPromo = { provider_id: '', tld: '', name: '', type: 'fixed_price', value: '', scope: 'first_year', applies_to: null, start_date: '', end_date: '', priority: 0, is_active: true };

export default function AdminPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [providers, setProviders] = useState([]);
  const [tlds, setTlds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPromo);
  const [appliesToReg, setAppliesToReg] = useState(true);
  const [appliesToRenew, setAppliesToRenew] = useState(false);
  const [appliesToTransfer, setAppliesToTransfer] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [promos, provs, ts] = await Promise.all([api.getPromotions(), api.getProviders(), api.getTlds()]);
      setPromotions(promos); setProviders(provs); setTlds(ts);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditing(null); setForm(emptyPromo);
    setAppliesToReg(true); setAppliesToRenew(false); setAppliesToTransfer(false);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      provider_id: p.provider_id, tld: p.tld, name: p.name, type: p.type,
      value: String(p.value), scope: p.scope, applies_to: p.applies_to,
      start_date: p.start_date || '', end_date: p.end_date || '',
      priority: p.priority || 0, is_active: p.is_active
    });
    setAppliesToReg(!p.applies_to || p.applies_to.includes('registration'));
    setAppliesToRenew(p.applies_to?.includes('renewal') || false);
    setAppliesToTransfer(p.applies_to?.includes('transfer') || false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const applies = [];
    if (appliesToReg) applies.push('registration');
    if (appliesToRenew) applies.push('renewal');
    if (appliesToTransfer) applies.push('transfer');
    const data = {
      ...form,
      value: parseFloat(form.value) || 0,
      priority: parseInt(form.priority) || 0,
      applies_to: applies.length === 3 ? null : (applies.length > 0 ? applies : null),
      start_date: form.start_date || null,
      end_date: form.end_date || null,
    };
    try {
      if (editing) {
        await api.updatePromotion(editing.id, data);
        toast.success('Aktion aktualisiert');
      } else {
        await api.createPromotion(data);
        toast.success('Aktion erstellt');
      }
      setDialogOpen(false); loadData();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Aktion löschen?')) return;
    try { await api.deletePromotion(id); toast.success('Gelöscht'); loadData(); } catch (e) { toast.error(e.message); }
  };

  const providerMap = Object.fromEntries(providers.map(p => [p.id, p.name]));

  const getValueDisplay = (p) => {
    if (p.type === 'fixed_price') return formatPrice(p.value);
    if (p.type === 'percentage_off') return `-${p.value}%`;
    if (p.type === 'amount_off') return `-${formatPrice(p.value)}`;
    return p.value;
  };

  return (
    <div data-testid="admin-promotions">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Aktionen</h1>
          <p className="text-sm text-muted-foreground mt-1">{promotions.length} Aktionen insgesamt</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="admin-crud-create-button">
          <Plus className="w-4 h-4" /> Neue Aktion
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-crud-table">
          {promotions.map(p => (
            <Card key={p.id} className="rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Tag className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-slate-900">{p.name}</span>
                      <Badge variant={p.is_active ? 'default' : 'secondary'} className="text-[10px]">{p.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <span>{providerMap[p.provider_id] || 'Unbekannt'}</span>
                      <span>·</span>
                      <span className="font-bold">{p.tld}</span>
                      <span>·</span>
                      <span>{PROMO_TYPE_LABELS[p.type]}: <strong>{getValueDisplay(p)}</strong></span>
                      <span>·</span>
                      <span>{PROMO_SCOPE_LABELS[p.scope]}</span>
                      {p.start_date && <><span>·</span><span>Ab: {formatDate(p.start_date)}</span></>}
                      {p.end_date && <><span>·</span><span>Bis: {formatDate(p.end_date)}</span></>}
                    </div>
                    {p.applies_to && (
                      <div className="flex gap-1 mt-1">
                        {p.applies_to.map(at => <Badge key={at} variant="outline" className="text-[9px]">{PRICE_TYPE_LABELS[at]}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-red-500"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {promotions.length === 0 && <div className="text-center py-10 text-muted-foreground">Keine Aktionen vorhanden</div>}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle data-testid="promotion-editor-form">{editing ? 'Aktion bearbeiten' : 'Neue Aktion'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Anbieter *</Label>
                <Select value={form.provider_id} onValueChange={v => setForm({...form, provider_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Wählen" /></SelectTrigger>
                  <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>TLD *</Label>
                <Select value={form.tld} onValueChange={v => setForm({...form, tld: v})}>
                  <SelectTrigger><SelectValue placeholder="Wählen" /></SelectTrigger>
                  <SelectContent>{tlds.map(t => <SelectItem key={t.tld} value={t.tld}>{t.tld}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. .de 1. Jahr nur 0,49€" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Typ</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed_price">Festpreis</SelectItem>
                    <SelectItem value="percentage_off">Prozent-Rabatt</SelectItem>
                    <SelectItem value="amount_off">Betrag-Rabatt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Wert *</Label>
                <Input type="number" step="0.01" value={form.value} onChange={e => setForm({...form, value: e.target.value})} placeholder={form.type === 'percentage_off' ? 'z.B. 20' : '0.00'} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Gültigkeit</Label>
                <Select value={form.scope} onValueChange={v => setForm({...form, scope: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_year">Nur 1. Jahr</SelectItem>
                    <SelectItem value="all_years">Alle Jahre</SelectItem>
                    <SelectItem value="one_time">Einmalig</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priorität</Label>
                <Input type="number" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} placeholder="0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Startdatum</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="promotion-start-date" />
              </div>
              <div>
                <Label>Enddatum</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="promotion-end-date" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Gilt für</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={appliesToReg} onChange={e => setAppliesToReg(e.target.checked)} /> Registrierung</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={appliesToRenew} onChange={e => setAppliesToRenew(e.target.checked)} /> Verlängerung</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={appliesToTransfer} onChange={e => setAppliesToTransfer(e.target.checked)} /> Transfer</label>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Aktiv</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={!form.provider_id || !form.tld || !form.name || !form.value} data-testid="admin-save-button">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
